import React, { useState, useEffect } from 'react';
import { formsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import { Plus, FileText, FileDown, Loader2, ChevronDown, Check, RotateCcw, Trash2, Send, Save, Mail, Copy, X, PenLine, Eye } from 'lucide-react';
import { FormFieldRenderer } from './FormFieldRenderer';
import { filterVisibleFields, evaluateVisibility } from '../lib/formVisibility';
import { groupFieldsIntoSections } from '../lib/formSections';
import { Plus as PlusIcon, X as XIcon } from 'lucide-react';

/**
 * JobFormSubmissions — Renders form submissions section for a job.
 * Props: jobId, readOnly (when job is completed/cancelled)
 */
export function JobFormSubmissions({ jobId, readOnly = false }) {
  const [submissions, setSubmissions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAttach, setShowAttach] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [openSubs, setOpenSubs] = useState({});

  const fetchData = async () => {
    try {
      const [subsRes, tplRes] = await Promise.all([
        formsAPI.listSubmissions({ job_id: jobId }),
        formsAPI.listTemplates({ active_only: true }),
      ]);
      setSubmissions(subsRes.data?.submissions || []);
      setTemplates(tplRes.data?.templates || []);
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [jobId]);

  const handleAttach = async (templateId) => {
    setAttaching(true);
    try {
      await formsAPI.createSubmission({ template_id: templateId, job_id: jobId });
      toast.success('Form attached');
      setShowAttach(false);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to attach form');
    }
    setAttaching(false);
  };

  const toggleOpen = (id) => setOpenSubs(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="space-y-3" data-testid="job-forms-section">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-1.5"><FileText className="w-4 h-4" /> Forms ({submissions.length})</h3>
        {!readOnly && (
          <Button variant="outline" size="sm" onClick={() => setShowAttach(true)} data-testid="attach-form-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Attach Form
          </Button>
        )}
      </div>

      {submissions.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-4">No forms attached to this job</p>
      ) : (
        submissions.map(sub => (
          <SubmissionCard
            key={sub.id}
            submission={sub}
            isOpen={openSubs[sub.id]}
            onToggle={() => toggleOpen(sub.id)}
            readOnly={readOnly}
            onRefresh={fetchData}
          />
        ))
      )}

      {/* Attach Template Dialog */}
      <Dialog open={showAttach} onOpenChange={setShowAttach}>
        <DialogContent>
          <DialogHeader><DialogTitle>Attach Form Template</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {templates.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No active templates. Create one first.</p>
            ) : (
              templates.map(t => (
                <button
                  key={t.id}
                  onClick={() => handleAttach(t.id)}
                  disabled={attaching}
                  className="w-full text-left p-3 rounded-lg border hover:bg-muted/40 transition-colors disabled:opacity-50"
                  data-testid={`attach-template-${t.id}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{t.name}</span>
                    {t.category && <Badge variant="outline" className="text-xs">{t.category}</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground">{t.fields?.length || 0} fields{t.description ? ` — ${t.description}` : ''}</p>
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SubmissionCard({ submission, isOpen, onToggle, readOnly, onRefresh }) {
  const { isOwner, isOffice } = useAuth();
  const canManageSignature = !!(isOwner || isOffice);
  const [data, setData] = useState(submission.data_json || {});
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [action, setAction] = useState(null);          // CustomerAction object
  const [reviewLink, setReviewLink] = useState(null);  // shown once after send
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [signEmail, setSignEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const isSubmitted = submission.status === 'submitted';
  const hasActiveAction = !!(action && (action.status === 'sent' || action.status === 'viewed'));
  const isSigned = !!(action && action.status === 'signed');
  // Form is "locked" once status==submitted OR there is an active customer action
  const isReadOnly = readOnly || isSubmitted;

  // Pull the latest CustomerAction once submission is in `submitted` state
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isSubmitted) { setAction(null); return; }
      try {
        const res = await formsAPI.getSignatureAction(submission.id);
        if (!cancelled) setAction(res.data?.action || null);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [submission.id, isSubmitted, submission.status]);

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      await formsAPI.updateSubmission(submission.id, { data_json: data });
      toast.success('Draft saved');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = (detail && typeof detail === 'object' && detail.message) ? detail.message : (typeof detail === 'string' ? detail : 'Failed to save');
      toast.error(msg);
    }
    setSaving(false);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await formsAPI.updateSubmission(submission.id, { data_json: data });
      await formsAPI.submitForm(submission.id);
      toast.success('Form submitted');
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = (detail && typeof detail === 'object' && detail.message) ? detail.message : (typeof detail === 'string' ? detail : 'Failed to submit');
      toast.error(msg);
    }
    setSaving(false);
  };

  const handleReopen = async () => {
    try {
      const res = await formsAPI.reopenForm(submission.id);
      const invalidated = res?.data?.invalidated_signatures || 0;
      toast.success(invalidated > 0
        ? `Form reopened — previous customer signature invalidated`
        : 'Form reopened for editing');
      setAction(null);
      setReviewLink(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reopen');
    }
  };

  const handleSendForSignature = async () => {
    setSending(true);
    try {
      const res = await formsAPI.sendForSignature(submission.id, {
        recipient_email: signEmail.trim() || undefined,
      });
      setAction(res.data);
      setReviewLink(res.data.review_url);
      setShowSendDialog(false);
      setSignEmail('');
      if (res.data.email_sent) {
        toast.success('Sent to customer for signature');
      } else if (res.data.recipient_email) {
        toast.warning('Link generated — email send may have failed. Use the copy-link below.');
      } else {
        toast.success('Link generated — copy the link below to share with your customer.');
      }
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = (detail && typeof detail === 'object' && detail.message) ? detail.message : (typeof detail === 'string' ? detail : 'Failed to generate link');
      toast.error(msg);
    }
    setSending(false);
  };

  const handleCopyLink = async (link) => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link copied');
    } catch { toast.error('Could not copy — please copy manually'); }
  };

  const handleCancelSignature = async () => {
    try {
      await formsAPI.cancelSignature(submission.id);
      toast.success('Signature request canceled');
      setAction(null);
      setReviewLink(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  const handleDelete = async () => {
    try {
      await formsAPI.deleteSubmission(submission.id);
      toast.success('Form removed');
      onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = (detail && typeof detail === 'object' && detail.message) ? detail.message : (typeof detail === 'string' ? detail : 'Failed to remove');
      toast.error(msg);
    }
    setShowDeleteConfirm(false);
  };

  // Phase 4: PDF generation. Two paths:
  //  - "Download PDF" → streams the latest snapshot inline (always works,
  //    regenerated on the fly).
  //  - "Regenerate & attach" → forces a fresh PDF, replaces the prior
  //    JobAttachment, refreshes the parent so Photos & Files updates.
  const handleDownloadPdf = async () => {
    setGeneratingPdf(true);
    try {
      const res = await formsAPI.downloadPdf(submission.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Revoke after a short delay to let the new tab pick it up
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };
  const handleRegeneratePdf = async () => {
    setGeneratingPdf(true);
    try {
      const res = await formsAPI.generatePdf(submission.id);
      toast.success(`PDF attached: ${res.data?.file_name || 'form.pdf'}`);
      // Refresh the surrounding job page so the Photos & Files list picks
      // up the new attachment.
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to attach PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const updateFieldValue = (key, value) => setData(prev => ({ ...prev, [key]: value }));
  const fields = submission.fields || [];

  // Phase 3 — group into repeatable / non-repeatable sections.
  const groups = groupFieldsIntoSections(fields);

  // Per-instance helpers for repeatable groups.
  const updateInstance = (sectionKey, idx, fieldKey, value) => {
    setData(prev => {
      const arr = Array.isArray(prev[sectionKey]) ? [...prev[sectionKey]] : [];
      const inst = { ...(arr[idx] || {}), [fieldKey]: value };
      arr[idx] = inst;
      return { ...prev, [sectionKey]: arr };
    });
  };
  const addInstance = (sectionKey) => {
    setData(prev => {
      const arr = Array.isArray(prev[sectionKey]) ? [...prev[sectionKey], {}] : [{}];
      return { ...prev, [sectionKey]: arr };
    });
  };
  const removeInstance = (sectionKey, idx) => {
    setData(prev => {
      const arr = Array.isArray(prev[sectionKey]) ? prev[sectionKey].filter((_, i) => i !== idx) : [];
      return { ...prev, [sectionKey]: arr };
    });
  };

  // Status badge
  const statusBadge = isSigned ? (
    <Badge className="text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" data-testid={`submission-status-${submission.id}`}>
      <Check className="w-3 h-3 mr-0.5" /> Signed by customer
    </Badge>
  ) : hasActiveAction ? (
    <Badge className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" data-testid={`submission-status-${submission.id}`}>
      {action.status === 'viewed' ? <><Eye className="w-3 h-3 mr-0.5" /> Viewed by customer</> : <><Send className="w-3 h-3 mr-0.5" /> Sent for signature</>}
    </Badge>
  ) : (
    <Badge variant={isSubmitted ? 'default' : 'secondary'} className="text-xs" data-testid={`submission-status-${submission.id}`}>
      {isSubmitted ? <><Check className="w-3 h-3 mr-0.5" /> Submitted</> : 'Draft'}
    </Badge>
  );

  return (
    <Card className={isSigned ? 'border-emerald-300 dark:border-emerald-900' : (isSubmitted ? 'border-green-200 dark:border-green-900' : '')} data-testid={`submission-card-${submission.id}`}>
      <Collapsible open={isOpen} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors py-3">
            <div className="flex items-center gap-2">
              <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-0' : '-rotate-90'}`} />
              <CardTitle className="text-sm flex-1">{submission.template_name || 'Form'}</CardTitle>
              {statusBadge}
            </div>
            {submission.submitter_name && <p className="text-xs text-muted-foreground ml-6">by {submission.submitter_name}</p>}
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-4 pt-0">
            {groups.map((group, gi) => (
              <SectionGroup
                key={group.section ? group.section.id : `g${gi}`}
                group={group}
                allFields={fields}
                data={data}
                onChangeField={updateFieldValue}
                onChangeInstance={updateInstance}
                onAddInstance={addInstance}
                onRemoveInstance={removeInstance}
                readOnly={isReadOnly}
                submissionId={submission.id}
                onRefresh={onRefresh}
              />
            ))}

            {/* Signature recap (signed) */}
            {isSigned && action && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 dark:border-emerald-900/50 dark:bg-emerald-900/20 p-3 space-y-2" data-testid={`signed-recap-${submission.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="text-xs">
                    <p className="font-medium text-emerald-900 dark:text-emerald-200">Signed by {action.signer_name}</p>
                    <p className="text-emerald-700/80 dark:text-emerald-400/80">{new Date(action.signed_at).toLocaleString()}{action.signer_email ? ` · ${action.signer_email}` : ''}</p>
                  </div>
                </div>
                {action.signature_data_url && (
                  <img
                    src={action.signature_data_url}
                    alt={`Signature of ${action.signer_name}`}
                    className="bg-white border border-emerald-200 rounded-md max-h-28"
                    data-testid={`signature-image-${submission.id}`}
                  />
                )}
              </div>
            )}

            {/* Active signature request */}
            {hasActiveAction && action && (
              <div className="rounded-md border border-blue-200 bg-blue-50 dark:border-blue-900/50 dark:bg-blue-900/20 p-3 space-y-2" data-testid={`pending-signature-${submission.id}`}>
                <div className="text-xs space-y-0.5">
                  <p className="font-medium text-blue-900 dark:text-blue-200">
                    {action.status === 'viewed' ? 'Customer has viewed this form' : 'Awaiting customer signature'}
                  </p>
                  {action.recipient_email && <p className="text-blue-700/80 dark:text-blue-400/80">Sent to {action.recipient_email}</p>}
                  {action.expires_at && <p className="text-blue-700/60 dark:text-blue-400/60">Expires {new Date(action.expires_at).toLocaleString()}</p>}
                </div>
                {reviewLink && (
                  <div className="flex items-center gap-2 bg-white dark:bg-slate-900 border border-blue-200 rounded p-2">
                    <code className="text-[11px] flex-1 truncate" data-testid={`review-link-${submission.id}`}>{reviewLink}</code>
                    <Button size="sm" variant="outline" onClick={() => handleCopyLink(reviewLink)} data-testid={`copy-link-${submission.id}`}>
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
                {canManageSignature && (
                  <div className="flex items-center gap-2 pt-1">
                    <Button size="sm" variant="outline" onClick={() => setShowSendDialog(true)} data-testid={`resend-signature-${submission.id}`}>
                      <Send className="w-3.5 h-3.5 mr-1" /> Resend / new link
                    </Button>
                    <Button size="sm" variant="ghost" className="text-rose-600" onClick={handleCancelSignature} data-testid={`cancel-signature-${submission.id}`}>
                      <X className="w-3.5 h-3.5 mr-1" /> Cancel
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
              {!isReadOnly && (
                <>
                  <Button size="sm" variant="outline" onClick={handleSaveDraft} disabled={saving} data-testid={`save-draft-${submission.id}`}>
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save Draft
                  </Button>
                  <Button size="sm" onClick={handleSubmit} disabled={saving} data-testid={`submit-form-${submission.id}`}>
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />} Submit
                  </Button>
                </>
              )}
              {/* Send-for-signature: only when submitted, no live action, and user is owner/office */}
              {isSubmitted && !hasActiveAction && !isSigned && canManageSignature && !readOnly && (
                <Button size="sm" onClick={() => setShowSendDialog(true)} data-testid={`send-for-signature-${submission.id}`}>
                  <PenLine className="w-3.5 h-3.5 mr-1" /> Send to Customer for Signature
                </Button>
              )}
              {/* Reopen: when submitted (and possibly signed), only owner/office */}
              {isSubmitted && canManageSignature && !readOnly && (
                <Button size="sm" variant="outline" onClick={handleReopen} data-testid={`reopen-form-${submission.id}`}>
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> {isSigned || hasActiveAction ? 'Reopen (invalidates signature)' : 'Reopen'}
                </Button>
              )}
              {/* Phase 4: Download PDF + Regenerate & attach (owner/office only,
                  only on submitted forms — drafts have nothing meaningful yet). */}
              {isSubmitted && canManageSignature && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDownloadPdf}
                    disabled={generatingPdf}
                    data-testid={`download-pdf-${submission.id}`}
                  >
                    {generatingPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <FileDown className="w-3.5 h-3.5 mr-1" />} Download PDF
                  </Button>
                  {!readOnly && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleRegeneratePdf}
                      disabled={generatingPdf}
                      data-testid={`regenerate-pdf-${submission.id}`}
                      title="Regenerate the PDF and attach it to the job's Photos & Files."
                    >
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Regenerate &amp; attach
                    </Button>
                  )}
                </>
              )}
              <div className="flex-1" />
              {!readOnly && (
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setShowDeleteConfirm(true)} data-testid={`delete-submission-${submission.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>

      {/* Send-for-signature dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send for customer signature</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              We'll generate a secure link for your customer to review and sign this form. If an email address is on file
              we'll also email the link.
            </p>
            <div>
              <Label htmlFor="sig-email" className="text-xs">Customer email <span className="text-muted-foreground">(optional — leave blank for copy-link only)</span></Label>
              <Input
                id="sig-email"
                type="email"
                value={signEmail}
                onChange={e => setSignEmail(e.target.value)}
                placeholder="customer@example.com"
                disabled={sending}
                data-testid={`sig-recipient-email-${submission.id}`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)} disabled={sending}>Cancel</Button>
            <Button onClick={handleSendForSignature} disabled={sending} data-testid={`confirm-send-signature-${submission.id}`}>
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Mail className="w-4 h-4 mr-1" />} Generate & Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader><DialogTitle>Remove this form?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This will permanently remove the form and its data from this job.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// FormField was inlined here — replaced by the shared <FormFieldRenderer />
// component which supports the Phase 1a Advanced Inspection Form types.

// ─── Phase 3 — Repeating Sections ─────────────────────────────────────────
function SectionGroup({
  group, allFields, data, onChangeField, onChangeInstance,
  onAddInstance, onRemoveInstance, readOnly, submissionId, onRefresh,
}) {
  const section = group.section;
  // Phase 2.x: visible_if can be set on a section_header to hide the entire
  // group (header + child fields + repeatable instances).
  if (section && !evaluateVisibility(section, data, allFields)) {
    return null;
  }
  // Render the section header itself first (if present).
  const headerNode = section ? (
    <FormFieldRenderer
      key={`${section.id}-hdr`}
      field={section}
      value={null}
      onChange={() => {}}
      readOnly
      submissionId={submissionId}
    />
  ) : null;

  if (!group.repeatable) {
    // Flat group — apply visibility filter to each child individually.
    const visible = filterVisibleFields(group.fields, data);
    return (
      <div className="space-y-4">
        {headerNode}
        {visible.map(f => (
          <FormFieldRenderer
            key={f.id}
            field={f}
            value={data[f.field_key]}
            onChange={(val) => onChangeField(f.field_key, val)}
            readOnly={readOnly}
            submissionId={submissionId}
            onFilesChanged={onRefresh}
          />
        ))}
      </div>
    );
  }

  // Repeatable group — render an instance for each entry under
  // data[section.field_key]. Default to a single empty instance so the form
  // is usable straight away.
  const sectionKey = section.field_key;
  const rawInstances = Array.isArray(data[sectionKey]) ? data[sectionKey] : [];
  const instances = rawInstances.length > 0 ? rawInstances : [{}];
  const max = group.max || 20;
  const canAdd = !readOnly && instances.length < max;
  const canRemove = !readOnly && instances.length > 1;

  return (
    <div className="space-y-3" data-testid={`repeat-section-${sectionKey}`}>
      {headerNode}
      {instances.map((inst, idx) => {
        const merged = { ...data, ...inst };
        const visible = filterVisibleFields(group.fields, merged);
        return (
          <div
            key={`${sectionKey}-${idx}`}
            className="rounded-lg border border-violet-200 bg-violet-50/30 p-3 space-y-3"
            data-testid={`repeat-instance-${sectionKey}-${idx}`}
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-violet-900">
                {group.label || 'Instance'} {idx + 1}
              </p>
              {canRemove && (
                <button
                  type="button"
                  onClick={() => onRemoveInstance(sectionKey, idx)}
                  className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-700"
                  data-testid={`repeat-remove-${sectionKey}-${idx}`}
                >
                  <XIcon className="w-3 h-3" /> Remove
                </button>
              )}
            </div>
            {visible.map(f => (
              <FormFieldRenderer
                key={f.id}
                field={f}
                value={inst[f.field_key]}
                onChange={(val) => onChangeInstance(sectionKey, idx, f.field_key, val)}
                readOnly={readOnly}
                submissionId={submissionId}
                onFilesChanged={onRefresh}
                fileUploadParams={{ section_key: sectionKey, instance_index: idx }}
              />
            ))}
          </div>
        );
      })}
      {canAdd && (
        <button
          type="button"
          onClick={() => onAddInstance(sectionKey)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-violet-300 text-violet-700 hover:bg-violet-50 text-sm font-medium"
          data-testid={`repeat-add-${sectionKey}`}
        >
          <PlusIcon className="w-3.5 h-3.5" /> Add another {group.label || 'instance'}
        </button>
      )}
      {readOnly && instances.length === 0 && (
        <p className="text-xs text-slate-400 italic px-1">No {group.label?.toLowerCase() || 'instances'} added.</p>
      )}
    </div>
  );
}
